import { RET, ZERO_SCORE_DISTANCE } from "./constants.js";
import { popcount32 } from "./numeric.js";

/* ===========================================================================
   Retrieval — the fused funnel of cardvision/recognize/retrieve.py.
   =========================================================================== */

export const gallery = {
  groups: 0,
  embedDim: 0,
  hasSignature: false,
  hasEmbeddings: false,
  hashes: null,
  phash256: null,
  colour: null,
  tiles: null,
  embeddings: null,
  embedScales: null,
  catalogue: null,
  //: `"set_code	frame" -> {symbol: [lo, hi], samples}` — the printed-symbol references
  //: the decision layer disambiguates reprints with. Null when the bundle ships none.
  setSymbols: null,
  calibration: null,
};

export const HASH_WEIGHTS = (() => {
  const w = [RET.weightDhashArt, RET.weightPhashArt, RET.weightDhashFull, RET.weightPhashFull];
  const total = w.reduce((a, b) => a + b, 0);
  return total > 0 ? w.map((v) => v / total) : w;
})();

export const distanceToScore = (d) => Math.max(0, 1 - d / ZERO_SCORE_DISTANCE);

/** Indices of the `poolSize` smallest values — np.argpartition's role here. */
/**
 * The `poolSize` rows with the smallest values — numpy's argpartition, not its sort.
 *
 * Ordering all 66,695 rows to read the first 512 of them was the most expensive thing
 * retrieval did: 12.4 ms a call against 1.2 ms for a quickselect, and it runs four
 * times per card (two pools, two orientations). The `a < b` tie-break is what makes
 * the result the *exact* set a stable sort returns rather than merely an equivalent
 * one — legacy distances are integer bit counts times fixed weights, so ties on the
 * pool boundary are common, and without it which of two equidistant rows got scored
 * would depend on where the partition happened to land.
 */
export function smallestRows(values, poolSize) {
  const n = values.length;
  if (n <= poolSize) return Array.from({ length: n }, (_, i) => i);
  const rows = new Int32Array(n);
  for (let i = 0; i < n; i++) rows[i] = i;
  const less = (a, b) => values[a] < values[b] || (values[a] === values[b] && a < b);
  const k = poolSize - 1;
  let lo = 0,
    hi = n - 1;
  while (lo < hi) {
    const pivot = rows[(lo + hi) >> 1];
    let i = lo,
      j = hi;
    while (i <= j) {
      while (less(rows[i], pivot)) i++;
      while (less(pivot, rows[j])) j--;
      if (i <= j) {
        const swap = rows[i];
        rows[i] = rows[j];
        rows[j] = swap;
        i++;
        j--;
      }
    }
    if (k <= j) hi = j;
    else if (k >= i) lo = i;
    else break;
  }
  return rows.subarray(0, poolSize);
}

/**
 * Embedding distances for every orientation of one card, in a single pass over the matrix.
 *
 * The scan is bound by streaming 34 MB of int8, not by the multiplies, so scoring both
 * orientations against a row while that row is in cache costs 27 ms where two separate
 * passes cost 46. The saving is in the *loop shape*, not in the arithmetic: a general
 * `for (q of queries)` around the dot product measured 47 ms — the same as two passes —
 * because the accumulators stop living in registers. Hence one hand-written case per
 * arity rather than the tidy loop, and only the two arities that occur.
 *
 * Returns one array per input, aligned with it, `null` where a vector is missing or does
 * not match the index (a swapped embedder.onnx). Scoring on a mismatched model would be
 * meaningless; the hash channels still work, so the caller degrades to them.
 */
export function embedDistances(vectors) {
  const out = vectors.map(() => null);
  if (!gallery.hasEmbeddings) return out;
  const use = [];
  vectors.forEach((vector, i) => {
    if (vector && vector.length === gallery.embedDim) use.push(i);
  });
  if (!use.length) return out;

  const n = gallery.groups,
    dim = gallery.embedDim;
  const data = gallery.embeddings,
    scales = gallery.embedScales;
  const similarity = use.map(() => new Float32Array(n));
  const best = use.map(() => -Infinity);

  if (use.length === 1) {
    const query = vectors[use[0]],
      sim = similarity[0];
    let best0 = -Infinity;
    for (let i = 0; i < n; i++) {
      let acc = 0;
      const base = i * dim;
      for (let d = 0; d < dim; d++) acc += data[base + d] * query[d];
      const value = acc * scales[i];
      sim[i] = value;
      if (value > best0) best0 = value;
    }
    best[0] = best0;
  } else {
    // Three or more orientations never happen — normalize.orientations is two — so the
    // pair is fused and anything beyond it falls back to one query at a time.
    for (let q = 0; q + 1 < use.length; q += 2) {
      const a = vectors[use[q]],
        b = vectors[use[q + 1]];
      const simA = similarity[q],
        simB = similarity[q + 1];
      let bestA = -Infinity,
        bestB = -Infinity;
      for (let i = 0; i < n; i++) {
        const base = i * dim;
        let accA = 0,
          accB = 0;
        for (let d = 0; d < dim; d++) {
          const v = data[base + d];
          accA += v * a[d];
          accB += v * b[d];
        }
        const scale = scales[i];
        const valueA = accA * scale,
          valueB = accB * scale;
        simA[i] = valueA;
        simB[i] = valueB;
        if (valueA > bestA) bestA = valueA;
        if (valueB > bestB) bestB = valueB;
      }
      best[q] = bestA;
      best[q + 1] = bestB;
    }
    if (use.length % 2) {
      const query = vectors[use[use.length - 1]],
        sim = similarity[use.length - 1];
      let bestLast = -Infinity;
      for (let i = 0; i < n; i++) {
        let acc = 0;
        const base = i * dim;
        for (let d = 0; d < dim; d++) acc += data[base + d] * query[d];
        const value = acc * scales[i];
        sim[i] = value;
        if (value > bestLast) bestLast = value;
      }
      best[use.length - 1] = bestLast;
    }
  }

  // The margin behind this query's own leader, not absolute cosine: every Magic card
  // shares frame and layout, so absolute cosines of the true and best-wrong references
  // sit on top of each other while the ranking stays strong.
  use.forEach((target, q) => {
    const distance = new Float32Array(n);
    const sim = similarity[q],
      leader = best[q];
    for (let i = 0; i < n; i++) distance[i] = (leader - sim[i]) * RET.embedScale;
    out[target] = distance;
  });
  return out;
}

/**
 * Score one query against the whole gallery.
 *
 * The legacy hashes scan the full corpus; the rich signature components score only the
 * best `enrichedPool` rows of that pass, and the embedding channel — already scanned
 * across every orientation by `embedDistances` — contributes its own `embedPool`. Rows
 * outside the union stay at infinity. This is the funnel from HashRetriever._combined,
 * weights and all.
 */
export function retrieve(signature, embedDistance) {
  const n = gallery.groups;
  const perHash = new Uint8Array(n * 4);
  const legacy = new Float32Array(n);
  const table = gallery.hashes;
  for (let i = 0; i < n; i++) {
    let fused = 0;
    for (let k = 0; k < 4; k++) {
      const lo = table[i * 8 + k * 2] ^ signature.hashes[k][0];
      const hi = table[i * 8 + k * 2 + 1] ^ signature.hashes[k][1];
      const bits = popcount32(lo >>> 0) + popcount32(hi >>> 0);
      perHash[i * 4 + k] = bits;
      fused += HASH_WEIGHTS[k] * bits;
    }
    legacy[i] = fused;
  }

  const useSignature = gallery.hasSignature;
  const useEmbedding = embedDistance != null;

  if (!useSignature && !useEmbedding) return rank(legacy, perHash, null);

  const pool = new Set(smallestRows(legacy, RET.enrichedPool));
  if (useEmbedding) for (const row of smallestRows(embedDistance, RET.embedPool)) pool.add(row);

  const rows = Array.from(pool);
  const combined = new Float32Array(n).fill(Infinity);
  const channels = [];
  const weights = [RET.weightLegacy];
  channels.push(rows.map((i) => legacy[i]));

  if (useSignature) {
    weights.push(RET.weightPhash256, RET.weightColour, RET.weightTiles);
    channels.push(
      rows.map((i) => {
        let bits = 0;
        for (let k = 0; k < 4; k++) {
          bits +=
            popcount32((gallery.phash256[i * 8 + k * 2] ^ signature.phash256[k][0]) >>> 0) +
            popcount32((gallery.phash256[i * 8 + k * 2 + 1] ^ signature.phash256[k][1]) >>> 0);
        }
        return bits / 4; // 0-256 bits onto the 0-64 scale
      }),
    );
    channels.push(
      rows.map((i) => {
        let acc = 0;
        for (let k = 0; k < 32; k++)
          acc += Math.abs(gallery.colour[i * 32 + k] - signature.colour[k]);
        return (acc / 32) * (64 / 255);
      }),
    );
    channels.push(
      rows.map((i) => {
        const per = [];
        for (let k = 0; k < 9; k++) {
          per.push(
            popcount32((gallery.tiles[i * 18 + k * 2] ^ signature.tiles[k][0]) >>> 0) +
              popcount32((gallery.tiles[i * 18 + k * 2 + 1] ^ signature.tiles[k][1]) >>> 0),
          );
        }
        // Trimmed mean: a foil streak or a thumb costs two tiles, not the whole distance.
        per.sort((a, b) => a - b);
        const keep = per.length - Math.max(0, Math.min(RET.tileTrim, per.length - 1));
        let acc = 0;
        for (let k = 0; k < keep; k++) acc += per[k];
        return acc / keep;
      }),
    );
  }
  if (useEmbedding) {
    weights.push(RET.weightEmbed);
    channels.push(rows.map((i) => embedDistance[i]));
  }

  const total = weights.reduce((a, b) => a + b, 0);
  for (let r = 0; r < rows.length; r++) {
    let fused = 0;
    for (let c = 0; c < channels.length; c++) fused += weights[c] * channels[c][r];
    combined[rows[r]] = fused / total;
  }
  return rank(combined, perHash, embedDistance, { rows, channels, weights });
}

export function rank(combined, perHash, embedDistance, breakdown) {
  const order = Array.from({ length: combined.length }, (_, i) => i)
    .filter((i) => Number.isFinite(combined[i]))
    .sort((a, b) => combined[a] - combined[b])
    .slice(0, RET.shortlist);

  const perRow = breakdown ? new Map(breakdown.rows.map((row, r) => [row, r])) : null;
  const hits = [];
  for (const i of order) {
    if (combined[i] > RET.maxDistance) break;
    const hit = {
      row: i,
      distance: combined[i],
      score: distanceToScore(combined[i]),
      perHash: [perHash[i * 4], perHash[i * 4 + 1], perHash[i * 4 + 2], perHash[i * 4 + 3]],
      embedDistance: embedDistance ? embedDistance[i] : null,
      channels: null,
    };
    if (perRow && perRow.has(i)) {
      const r = perRow.get(i);
      hit.channels = breakdown.channels.map((channel, c) => ({
        weight: breakdown.weights[c],
        value: channel[r],
      }));
    }
    hits.push(hit);
  }
  return hits;
}
